import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import HeroIAdvize from "@/components/HeroIAdvize";
import ContentSectionIAdvize from "@/components/ContentSectionIAdvize";
import BlogPostsSection from "@/components/BlogPostsSection";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const IAdvize = () => {
  const [totalInscriptions, setTotalInscriptions] = useState(0);
  const [showCounter, setShowCounter] = useState(false);

  useEffect(() => {
    // Fetch setting to show/hide counter
    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "show_inscription_counter")
        .maybeSingle();

      if (!error && data?.setting_value && typeof data.setting_value === "object" && "enabled" in data.setting_value) {
        setShowCounter(data.setting_value.enabled as boolean);
      } else {
        setShowCounter(false);
      }
    };

    // Fetch initial count using secure function
    const fetchCount = async () => {
      const { data, error } = await supabase.rpc("get_inscription_count");

      if (!error && data !== null) {
        setTotalInscriptions(data);
      }
    };

    fetchSettings();
    fetchCount();

    // Subscribe to realtime changes
    const channel = supabase
      .channel("inscriptions-changes-iadvize")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "inscriptions",
        },
        () => {
          fetchCount();
        },
      )
      .subscribe((status, err) => {
        if (err) {
          console.debug("Realtime subscription error (non-critical):", err);
        }
      });

    return () => {
      supabase.removeChannel(channel).catch(() => {
        // Suppress channel removal errors
      });
    };
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "iAdvize : Action collective des conseillers",
    description: "Action collective des anciens conseillers iAdvize pour faire valoir leurs droits. Rejoignez le Collectif Ibbü.",
    url: "https://collectif-ibbu.org/iadvize",
    mainEntity: {
      "@type": "Organization",
      name: "Collectif Ibbü",
      description: "Collectif des anciens conseillers iAdvize/Ibbü",
    },
    about: {
      "@type": "Thing",
      name: "iAdvize",
      description: "Plateforme de messaging conversationnel pour e-commerce",
    },
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://collectif-ibbu.org",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "iAdvize",
        item: "https://collectif-ibbu.org/iadvize",
      },
    ],
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Qu'est-ce qu'iAdvize exactement ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "iAdvize est une entreprise française fondée à Nantes, spécialisée dans le messaging conversationnel pour le e-commerce. Via son programme Ibbü, iAdvize mettait en relation des conseillers indépendants avec des marques.",
        },
      },
      {
        "@type": "Question",
        name: "Comment fonctionnait la rémunération sur iAdvize ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Les conseillers iAdvize auraient été rémunérés selon différents modèles : rémunération à la conversation, au temps passé connecté, ou à la mission.",
        },
      },
      {
        "@type": "Question",
        name: "Quels problèmes les conseillers iAdvize ont-ils rencontré ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Plusieurs problématiques ont été signalées : baisse du volume de missions, ruptures soudaines de collaboration, décisions techniques impactant les revenus.",
        },
      },
      {
        "@type": "Question",
        name: "Comment rejoindre l'action collective contre iAdvize ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tous les conseillers ayant travaillé avec iAdvize peuvent rejoindre le Collectif Ibbü via le formulaire d'inscription. L'inscription est gratuite et confidentielle.",
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title="iAdvize : Action collective des conseillers | Collectif Ibbü"
        description="Conseillers iAdvize, rejoignez l'action collective pour faire valoir vos droits. Le Collectif Ibbü rassemble les anciens conseillers de la plateforme iAdvize."
        canonical="https://collectif-ibbu.org/iadvize"
        keywords="iAdvize, iAdvize avis, iAdvize conseillers, Ibbü, plateforme iAdvize, conseillers iAdvize, action collective iAdvize, droits conseillers iAdvize"
        structuredData={[structuredData, breadcrumbStructuredData, faqStructuredData]}
      />
      <div className="min-h-screen">
        <main>
          <HeroIAdvize totalInscriptions={totalInscriptions} showCounter={showCounter} />
          <ContentSectionIAdvize />
          <BlogPostsSection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default IAdvize;
